































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import {
  ActivityHistoryElement,
  ActivityHistoryStatus,
  ActivityHistoryType,
  DrivewealthAccountManagementType,
  OrderCancellationInitator
} from "@/shared/types";
import Pagination from "@/shared/components/ui/Pagination.vue";
import { Location } from "vue-router";
import moment from "moment";
import "moment-timezone";
import _ from "lodash";
import { formatCurrency } from "@/shared/currency";
import Button from "@/shared/components/ui/Button.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";

@Component({
  components: {
    Pagination,
    Button,
    GenericLoadingSpinner
  }
})
export default class Activity extends Vue {
  drivewealthStore = useDrivewealthStore();
  featureFlagStore = useFeatureFlagStore();
  notifierStore = useNotifierStore();

  @Prop() pageNumber: number;

  @Prop() routeToDrivewealthActivity: (pageNumber: number) => Location;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop() accountId: number;

  showCancelConfirm = false;
  bendOrderId: string;
  stockSymbol = "";
  amount = "";

  @Watch("$props", { deep: true })
  loadTable () {
    this.onPageNumberUpdated();
    if (this.accountId) {
      this.drivewealthStore.loadPendingActivityByAccount(
        {
          username: this.username, partnerId: this.partnerId,
          accountId: this.accountId
        });
    }
    this.drivewealthStore.loadAdvisoryFunds({ username: this.username, partnerId: this.partnerId });
  }

  pendingVisible = true;

  get pendingActivityLoadedForAccount (): boolean {
    return this.drivewealthStore.pendingActivityV2Loaded(this.accountId);
  }

  get pendingActivityLoadingForAccount (): boolean {
    return this.drivewealthStore.pendingActivityV2Loading(this.accountId);
  }

  get pendingActivityItemsForAccount (): ActivityHistoryElement[] {
    const pendingActivity = this.drivewealthStore.pendingActivityById(this.accountId);

    return pendingActivity ? pendingActivity.pendingOrders : [];
  }

  get isLoading () {
    return this.pendingActivityLoadingForAccount || this.drivewealthStore.completedActivityLoading ||
      this.drivewealthStore.advisoryFundsLoading;
  }

  get isManagedAccount () {
    return this.drivewealthStore.accountManagementType(this.accountId) ===
      DrivewealthAccountManagementType.RiaManaged;
  }

  get hasPendingActivity () {
    return this.pendingActivityLoadedForAccount &&
      this.pendingActivityItemsForAccount &&
      this.pendingActivityItemsForAccount.length > 0 &&
      !this.isManagedAccount;
  }

  get completedActivityText () {
    return this.isManagedAccount ? "Transaction History" : "Executed Transactions";
  }

  formattedShareAmount (shareAmount?: number) {
    return shareAmount === null ? "—" : shareAmount.toFixed(2);
  }

  get isCancelButtonEnabled (): boolean {
    return this.featureFlagStore.flags.bend24088EnableDrivewealthOrderCancelButton;
  }

  created () {
    this.onPageNumberUpdated();
    if (this.accountId) {
      this.drivewealthStore.loadPendingActivityByAccount(
        {
          username: this.username, partnerId: this.partnerId,
          accountId: this.accountId
        });
    }
    this.drivewealthStore.loadAdvisoryFunds({ username: this.username, partnerId: this.partnerId });
  }

  @Watch("drivewealthStore.pendingActivityV2.items")
  @Watch("drivewealthStore.pendingActivityLoaded")
  onPendingActivityLoaded () {
    this.pendingVisible = this.pendingActivityItemsForAccount &&
      this.pendingActivityItemsForAccount.length > 0;
  }

  @Watch("pageNumber")
  onPageNumberUpdated () {
    this.drivewealthStore.setCompletedActivityPageNumber(this.pageNumber);
    if (this.accountId) {
      this.drivewealthStore.loadCompletedActivityByAccount(
        { username: this.username, partnerId: this.partnerId, accountId: this.accountId, force: true });
    } else {
      this.drivewealthStore.loadCompletedActivity({ username: this.username, partnerId: this.partnerId });
    }
  }

  formatDate (date?: moment.Moment) {
    if (!date) {
      return "";
    }
    return moment(date).format("MMM D, YYYY");
  }

  formatDateTimeStamp (date?: moment.Moment) {
    if (!date) {
      return "";
    }
    const utcTime = moment.utc(date);
    const estTime = utcTime.clone().tz("America/New_York");
    return estTime.format("MMM D, YYYY HH:mm:ss z");
  }

  formatCurrency (currency, digits) {
    return formatCurrency(currency, digits);
  }

  displayOrderCancellationIniatorCopy (orderCancellationInitator: OrderCancellationInitator) {
    return orderCancellationInitator && orderCancellationInitator !== OrderCancellationInitator.None;
  }

  onPageChanged (pageNumber: number) {
    this.routeToDrivewealthActivity(pageNumber);
  }

  hasOverflowText (a: ActivityHistoryElement) {
    return !!a.comment || !!a.drivewealthDocument || a.shareAmount > 0;
  }

  createDownloadLink (documentId: number): void {
    this.drivewealthStore.loadDocumentInTab({ documentId, username: this.username, partnerId: this.partnerId });
  }

  getAmount (p: ActivityHistoryElement) {
    if (p.status === ActivityHistoryStatus.Pending && p.type === ActivityHistoryType.Sell && p.dollarAmount === 0) {
      return "Sale Pending";
    }

    return this.formatCurrency(p.dollarAmount, 2);
  }

  getDescriptiveType (type: string, investmentFeeDescription: string): string {
    if (type === ActivityHistoryType.BillingFee) {
      return investmentFeeDescription || "Investment Platform Fee";
    } else if (type === ActivityHistoryType.TransactionFee) {
      return "Fee";
    } else if (type === ActivityHistoryType.DividendReinvestment) {
      return "Dividend Reinvestment";
    }
    return type;
  }

  showShareInfo (row: ActivityHistoryElement): boolean {
    return row.shareAmount && row.type !== ActivityHistoryType.DividendReinvestment;
  }

  calculateCostPerShare (row: ActivityHistoryElement): string {
    if (row.shareAmount && row.type !== ActivityHistoryType.DividendReinvestment) {
      const costPerShare = row.dollarAmount / row.shareAmount;

      return this.formatCurrency(costPerShare, 2);
    }

    return "";
  }

  mapToTableItems (elements: ActivityHistoryElement[]) {
    return elements.map((p, i) => ({
      trade_date: this.formatDate(p.tradeDate),
      settlement_date: this.formatDate(p.settledDate),
      settlementDateTimeStamp: this.formatDateTimeStamp(p.settledDate),
      instrument: { symbol: p.stockSymbol, companyName: p.companyName },
      transaction_type: this.getDescriptiveType(p.type, p.investmentFeeDescription),
      dollarAmount: p.dollarAmount,
      amount: this.getAmount(p),
      shareAmount: p.shareAmount === null ? "—" : p.shareAmount.toFixed(8),
      comment: p.comment,
      drivewealthDocument: p.drivewealthDocument,
      id: p.status + i,
      status: p.status,
      statusDisplayCopy: this.getActivityHistoryStatusDisplayCopy(p.status),
      _showDetails: false,
      canExpand: this.hasOverflowText(p),
      showShareInfo: this.showShareInfo(p),
      bendOrderId: p.bendOrderId,
      isCancellable: p.isCancellable,
      isCancelOrderInProgress: p.isCancelOrderInProgress,
      orderCancellationInitator: p.orderCancellationInitator
    }));
  }

  completedTableFields = [
    {
      key: "toggle",
      label: "",
      thClass: "toggle-col",
      tdClass: "pt-3", // I cannot figure out how to vertically-center this!
      // using a flex-box messes up the borders, and won't fill the space
    },
    {
      key: "trade_date",
      thClass: "text-left",
      label: "Date Initiated"
    },
    {
      key: "settlement_date",
      thClass: "text-left",
      label: "Executed Date"
    },
    {
      key: "instrument",
      thClass: "text-left",
      label: "Investment"
    },
    {
      key: "transaction_type",
      thClass: "text-left"
    },
    {
      key: "statusDisplayCopy",
      label: "Status",
      thClass: "text-left"
    },
    {
      key: "amount",
      label: "Amount",
      thClass: "text-right"
    },
  ];

  get completedTableItems () {
    return this.mapToTableItems(this.drivewealthStore.completedActivity);
  }

  getActivityHistoryStatusDisplayCopy (status: ActivityHistoryStatus): string {
    switch (status) {
      case ActivityHistoryStatus.Canceled:
        return `Canceled`;
      case ActivityHistoryStatus.Failed:
        return `Failed`;
      case ActivityHistoryStatus.Settled:
        return `Executed`;
      case ActivityHistoryStatus.Pending:
        return `Pending`;
      case ActivityHistoryStatus.PartiallyExecuted:
        return `Partially Executed`;
      default:
        return `Executed`;
    }
  }

  pendingTableFields = [
    {
      key: "trade_date",
      label: "Date Initiated",
      thClass: "text-left"
    },
    {
      key: "instrument",
      thClass: "text-left",
      label: "Investment"
    },
    {
      key: "transaction_type",
      thClass: "text-left"
    },
    {
      key: "amount",
      label: "Amount",
      thClass: "text-left"
    },
    {
      key: "cancel",
      label: "",
      thClass: "text-right"
    }
  ];

  get pendingTableItems () {
    const orderedPendingItems = _.orderBy(this.pendingActivityItemsForAccount, "tradeDate", "desc");
    return this.mapToTableItems(orderedPendingItems);
  }

  onRowClicked (item: any, index: number) {
    if (item.canExpand) {
      item._showDetails = !item._showDetails;
    }
  }

  onCancel (bendOrderId: string, stockSymbol: string, amount: string) {
    this.bendOrderId = bendOrderId;
    this.stockSymbol = stockSymbol;
    this.amount = amount;
    this.showCancelConfirm = true;
  }

  cancelOrderCancel () {
    this.showCancelConfirm = false;
  }

  continueCancel () {
    this.drivewealthStore.createCancelOrderRequest(this.bendOrderId)
        .then(() => {
          this.notifierStore.setSuccess("Order was cancelled");
          this.drivewealthStore.loadPendingActivityByAccount(
              {
                username: this.username, partnerId: this.partnerId,
                accountId: this.accountId, force: true
              });
        })
        .finally(() => {
          this.showCancelConfirm = false;
        });
  }
}
